import { z } from 'zod';
import { FileInfo, MAX_FILES_PER_MESSAGE } from './files.js';
import { PersonRef } from './people.js';
/** Support desk (docs/adr/0013-support-desk.md). */
export const TicketAttachment = z.object({
    name: z.string(),
    size: z.number().int(),
    contentType: z.string(),
    /** The stored file, when it was imported (docs/adr/0014-file-uploads.md). */
    file: FileInfo.nullable(),
});
export const TicketEmail = z.object({
    id: z.uuid(),
    direction: z.enum(['in', 'out']),
    fromAddress: z.string(),
    fromName: z.string().nullable(),
    toAddresses: z.array(z.string()),
    subject: z.string(),
    bodyText: z.string(),
    /** Who on the team sent it (outbound only). */
    author: PersonRef.nullable(),
    /** False when the sender's domain failed DMARC: show "sender not verified". */
    senderVerified: z.boolean(),
    attachments: z.array(TicketAttachment),
    createdAt: z.string(),
});
export const TicketPerson = z.object({
    contactId: z.uuid(),
    email: z.string(),
    name: z.string().nullable(),
    /** The person who raised the ticket; everyone else is getting updates (copied or added). */
    requester: z.boolean(),
    /** Chose "email me less": no received or waiting-on-you emails. */
    emailLess: z.boolean(),
});
export const TicketPeople = z.object({ people: z.array(TicketPerson) });
export const AddTicketWatcherInput = z.object({ email: z.email().trim().toLowerCase().max(320), name: z.string().trim().max(200).optional() });
export const TicketConversation = z.object({
    requesterEmail: z.string().nullable(),
    /** Where replies come back to; customers can also write to the workspace address. */
    replyAddress: z.string().nullable(),
    firstResponseDueAt: z.string().nullable(),
    firstRespondedAt: z.string().nullable(),
    slaDueAt: z.string().nullable(),
    slaPaused: z.boolean(),
    emails: z.array(TicketEmail),
    /** Client contacts who get emails about the ticket (docs/adr/0027). Empty for guests. */
    people: z.array(TicketPerson),
});
export const ReplyToTicketInput = z.object({
    body: z.string().trim().min(1).max(20000),
    /** Set the ticket's status in the same step, e.g. Waiting on client or Resolved. */
    statusId: z.uuid().optional(),
    /** Ready uploads by the replier (docs/adr/0014). They go with the email and show on the client's ticket page. */
    fileIds: z.array(z.uuid()).max(MAX_FILES_PER_MESSAGE).default([]),
});
export const SupportInbox = z.object({
    /** e.g. brightlane@inbound.chapsi.app. Null when inbound email is not configured on this server. */
    address: z.string().nullable(),
    enabled: z.boolean(),
    inboxProjectId: z.uuid().nullable(),
});
export const UpdateSupportInboxInput = z.object({
    enabled: z.boolean().optional(),
    inboxProjectId: z.uuid().nullable().optional(),
});
const DOMAIN = /^(?=.{3,253}$)([a-z0-9-]+\.)+[a-z]{2,}$/;
export const EmailDomain = z.string().trim().toLowerCase().regex(DOMAIN, 'Enter a domain like example.com');
/** Free-mail domains can never route tickets to a client: everyone would match. */
export const FREE_MAIL_DOMAINS = ['gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'live.com', 'yahoo.com', 'icloud.com', 'me.com', 'bigpond.com', 'bigpond.net.au', 'optusnet.com.au', 'proton.me', 'protonmail.com'];
/** Removes quoted history from an email reply ("On Tue, Dana wrote:", "> …", Outlook headers). */
export function stripQuoted(text) {
    const lines = text.replace(/\r\n/g, '\n').split('\n');
    const cut = lines.findIndex((l, i) => {
        const t = l.trim();
        if (t.includes('##- Reply above this line -##'))
            return true;
        if (/^-{2,}\s*Original Message\s*-{2,}$/i.test(t))
            return true;
        if (/^On .{4,200}wrote:$/i.test(t))
            return true;
        if (/^On .{4,200}$/i.test(t) && /wrote:$/i.test(lines[i + 1]?.trim() ?? ''))
            return true;
        if (/^From: .+/i.test(t) && /^(Sent|Date): .+/i.test(lines[i + 1]?.trim() ?? ''))
            return true;
        if (t.startsWith('>') && lines.slice(i).every((x) => x.trim() === '' || x.trim().startsWith('>')))
            return true;
        return false;
    });
    const kept = (cut >= 0 ? lines.slice(0, cut) : lines).join('\n');
    return kept.replace(/\n{3,}/g, '\n\n').trim();
}
const SIGN_OFF = /^(thanks|thank you|thanks again|many thanks|thx|ta|cheers|regards|kind regards|best regards|warm regards|best|best wishes|all the best|sincerely|yours|speak soon|talk soon)[,.!]*$/i;
/**
 * Removes a signature from an email reply, after stripQuoted (docs/adr/0070): everything from a `-- ` delimiter, a
 * line of underscores or "Sent from my …" / "Get Outlook for …", then a trailing sign-off ("Thanks,") and the sender's
 * own name ("Nathan"), at most three such lines, and never the whole message.
 */
export function stripSignature(text, senderNames = []) {
    const lines = text.replace(/\r\n/g, '\n').split('\n');
    const cut = lines.findIndex((l) => {
        const t = l.trim();
        return /^--\s*$/.test(l) || /^_{3,}$/.test(t) || /^(sent from my|sent from outlook|get outlook for|sent via)\b/i.test(t);
    });
    const kept = cut >= 0 ? lines.slice(0, cut) : lines;
    const names = new Set(senderNames
        .flatMap((n) => {
        const full = n.trim().toLowerCase();
        return full ? [full, full.split(/\s+/)[0]] : [];
    })
        .filter((n) => n.length > 0));
    const isTrailer = (t) => {
        const s = t.trim().replace(/^[-–—~]\s*/, '').toLowerCase();
        if (!s)
            return false;
        if (SIGN_OFF.test(s))
            return true;
        const bare = s.replace(/[,.!]+$/, '');
        if (names.has(bare))
            return true;
        // "Thanks, Nathan" on one line.
        const m = /^([a-z ]+),\s*(.+)$/.exec(bare);
        return !!m && SIGN_OFF.test(m[1]) && names.has(m[2].trim());
    };
    let end = kept.length;
    for (let removed = 0; removed < 3;) {
        while (end > 0 && kept[end - 1].trim() === '')
            end--;
        if (end === 0 || !isTrailer(kept[end - 1]))
            break;
        const rest = kept.slice(0, end - 1).join('\n').trim();
        if (!rest)
            break;
        end--;
        removed++;
    }
    return kept.slice(0, end).join('\n').replace(/\n{3,}/g, '\n\n').trim();
}
//# sourceMappingURL=support.js.map